import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Lookup from '@/api/lookup/api-lookup';
import { Delete, Get, PostBlob, Post ,Put} from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { firstListItem, itemPerPage } from '@/constants/defaultValues';
import { apiPath } from '@/constants/defaultValues';
import { getLabel } from '@/helpers/Utils';

const initialState = {
  loading: false,
  AddWorkScheduleResponse: [],
  PaginatedWorkScheduleResponse: [],
  WorkScheduleList: [],
  notification: { show: false, code: '' },
  WorkScheduleItem: {},
  AddUpdateDeleteAssignment: {},
  AssignedScheduleList: [],
  error: '',
};

export const AddNewWorkSchedule = createAsyncThunk(
  'workSchedule/AddWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/Add'}`,
        data: { ...params, companyID: getCompanyID() },
      });
      thunkAPI.dispatch(
        fetchPaginatedWorkSchedule({
          pageNumber: 1,
          pageSize: itemPerPage,
          keyword: '',
        })
      );
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchPaginatedWorkSchedule = createAsyncThunk(
  'workSchedule/fetchPaginatedWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/GetAllPaginated'}`,
        data: {
          companyID: getCompanyID(),
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          keyWord: params.keyword,
          sortColumn: params.sortColumn,
          sortDirection: params.sortDirection
        },
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getWorkScheduleById = createAsyncThunk(
  'workSchedule/getWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const geWorkScheduleAssignmentById = createAsyncThunk(
  'workSchedule/geWorkScheduleAssignmentById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Operations/WorkScheduleAssignment/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


export const UpdateWorkSchedule = createAsyncThunk(
  'workSchedule/UpdateWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/Update'}`,
        data: { ...params, companyID: getCompanyID() },
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getAllSchedulesByCompanyId = createAsyncThunk(
  'workSchedule/getAllSchedulesByCompanyId',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetAll/${getCompanyID()}`,
      });
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          label: getLabel(item.nameAr, item.nameEn),
          value: item.id,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteWorkScheduleById = createAsyncThunk(
  'workSchedule/deleteWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/WorkSchedule/Delete/${params}`,
      });
      thunkAPI.dispatch(
        fetchPaginatedWorkSchedule({
          pageNumber: 1,
          pageSize: itemPerPage,
          keyword: '',
        })
      );
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const deleteAssignWorkScheduleById = createAsyncThunk(
  'workSchedule/deleteAssignWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Operations/WorkScheduleAssignment/Delete/${params?.id}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const AddWorkScheduleAssignment = createAsyncThunk(
  'workSchedule/AddWorkScheduleAssignment',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/WorkScheduleAssignment/Add'}`,
        data: params,
      });
      if (response?.data?.errCode === '200') {
        thunkAPI.dispatch(
          GetAssignedSchedules({
            pageNumber: 1,
            pageSize: itemPerPage,
            companyId: getCompanyID(),
            targetEmployees: {
              targetEmployeesType:
                params?.targetEmployees[0]?.targetEmployeesType,
            },
          })
        );
        return response?.data?.data;
      } else if (response?.data?.errCode === '600') {
        return response?.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const UpdateWorkScheduleAssignment = createAsyncThunk(
  'workSchedule/UpdateWorkScheduleAssignment',
  async (params, thunkAPI) => {
    try {
      const response = await Put({
        url: `${apiPath}api/Operations/WorkScheduleAssignment/Update/${params?.id}`,
        data: params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


export const GetAssignedSchedules = createAsyncThunk(
  'workSchedule/GetAssignedSchedules',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/WorkScheduleAssignment/Get'}`,
        data: params,
      });
      return response?.data?.data ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const workScheduleSlice = createSlice({
  name: 'workSchedule',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.AddWorkScheduleResponse = [];
      state.worksc;
      state.PaginatedWorkScheduleResponse = [];
      state.WorkScheduleItem = {};
      state.notification = { show: false };
      state.AssignedScheduleList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AddNewWorkSchedule.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(AddNewWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(AddNewWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchPaginatedWorkSchedule.pending, (state) => {
        state.loading = true;
        state.WorkScheduleItem = {};
        state.AddWorkScheduleResponse = [];
        // state.notification = {show : false, code : ""};
      })
      .addCase(fetchPaginatedWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = action.payload;
        state.error = '';
      })
      .addCase(fetchPaginatedWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.WorkScheduleItem = {};
      })
      .addCase(getWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = action.payload;
        state.error = '';
      })
      .addCase(getWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = {};
        state.error = action.payload ?? 'An error occurred';
      });
    
    builder
    .addCase(geWorkScheduleAssignmentById.pending, (state) => {
      state.loading = true;
      state.notification = { show: false, code: '' };
      state.WorkScheduleItem = {};
    })
    .addCase(geWorkScheduleAssignmentById.fulfilled, (state, action) => {
      state.loading = false;
      state.WorkScheduleItem = action.payload;
      state.error = '';
    })
    .addCase(geWorkScheduleAssignmentById.rejected, (state, action) => {
      state.loading = false;
      state.WorkScheduleItem = {};
      state.error = action.payload ?? 'An error occurred';
    });

    builder
      .addCase(UpdateWorkSchedule.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.AddWorkScheduleResponse = [];
      })
      .addCase(UpdateWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.AddWorkScheduleResponse = {
            ...action.payload,
            isUpdated: true
          };
        } else {
          state.AddWorkScheduleResponse = action.payload;
        }
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(UpdateWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getAllSchedulesByCompanyId.pending, (state) => {
        state.loading = true;
        state.WorkScheduleList = [];
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllSchedulesByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleList = action.payload;
        state.error = '';
      })
      .addCase(getAllSchedulesByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(deleteWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(deleteAssignWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteAssignWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200', fromAction: 'DELETE' };
        state.error = '';
      })
      .addCase(deleteAssignWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(AddWorkScheduleAssignment.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(AddWorkScheduleAssignment.fulfilled, (state, action) => {
        state.loading = false;
        if (action?.payload?.errCode && action?.payload?.errCode === '600') {
          state.notification = { show: true, code: 'overlapWorkSchedule' };
        } else {
          state.AddUpdateDeleteAssignment = { status: true };
          state.notification = { show: true, code: '200' };
        }
        state.error = '';
      })
      .addCase(AddWorkScheduleAssignment.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteAssignment = {};
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(UpdateWorkScheduleAssignment.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(UpdateWorkScheduleAssignment.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload){
          if (action?.payload?.errCode && action?.payload?.errCode === '600') {
            state.notification = { show: true, code: 'overlapWorkSchedule' };
          } else {
            state.AddUpdateDeleteAssignment = { status: true };
            state.notification = { show: true, code: '200' };
          }
        }
        state.error = '';
      })
      .addCase(UpdateWorkScheduleAssignment.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteAssignment = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetAssignedSchedules.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(GetAssignedSchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.AssignedScheduleList = action.payload;
        state.error = '';
      })
      .addCase(GetAssignedSchedules.rejected, (state, action) => {
        state.loading = false;
        state.AssignedScheduleList = [];
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem } = workScheduleSlice.actions;
export default workScheduleSlice.reducer;
